/**
 * Integrytas Group - Unified Notification Engine (UNE)
 * Page: UNE.ADMIN
 *
 * Administrator configuration: workspaces, event categories, role/global
 * defaults, and Mandatory rules (force a channel on; locks the user's toggle).
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'UNE.ADMIN';

type Pref = 'INAPP_ONLY' | 'EMAIL_ONLY' | 'BOTH' | 'NONE';
interface WsRow { une_workspace_id: number; internal_code: string; name: string; is_default: 'Y' | 'N'; is_active: 'Y' | 'N' }
interface CatRow { une_event_category_id: number; internal_code: string; name: string; sort_order: number; is_active: 'Y' | 'N' }
interface MandRow { une_mandatory_rule_id: number; une_event_category_id: number; channel: 'INAPP' | 'EMAIL'; une_workspace_id: number | null; is_active: 'Y' | 'N' }
interface RdRow { une_role_default_id: number; une_event_category_id: number; role_id: number | null; une_workspace_id: number | null; channel_pref: Pref; is_active: 'Y' | 'N' }
interface RoleLite { role_id: number; internal_code: string }

const WS_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',    defaultWidth: 160 },
  { key: 'name',          header: 'Name',    defaultWidth: 220 },
  { key: 'is_default',    header: 'Default', defaultWidth: 100 },
  { key: 'is_active',     header: 'Active',  defaultWidth: 100 },
];

export interface UneAdminPageProps { session: AuthSession }

export function UneAdminPage({ session }: UneAdminPageProps) {
  return (
    <PersonalizationProvider pageCode="UNE.ADMIN" session={session}>
      <UneAdminPageInner session={session} />
    </PersonalizationProvider>
  );
}

function UneAdminPageInner({ session }: UneAdminPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [ws, setWs] = useState<WsRow[]>([]);
  const [cats, setCats] = useState<CatRow[]>([]);
  const [mand, setMand] = useState<MandRow[]>([]);
  const [rds, setRds] = useState<RdRow[]>([]);
  const [roles, setRoles] = useState<RoleLite[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wsFilter, setWsFilter] = useState('');
  // §17.B5: per-column multi-value facets for the Workspaces list.
  const [wsFacets, setWsFacets] = useState<{ deflt: string[]; active: string[] }>({ deflt: [], active: ['Y'] });

  const wsFacetOptions = useMemo(() => ({
    deflt:  [{ value: 'Y', label: t('une_admin.default', 'default') }, { value: 'N', label: t('une_admin.not_default', 'not default') }],
    active: [{ value: 'Y', label: t('une_admin.active', 'active') }, { value: 'N', label: t('une_admin.inactive', 'inactive') }],
  }), [t]);

  const wsFacetsActive = wsFacets.deflt.length + wsFacets.active.length > 0;
  const anyWsFilterActive = wsFacetsActive || wsFilter.trim().length > 0;
  const clearWsFilters = () => { setWsFilter(''); setWsFacets({ deflt: [], active: [] }); };

  const filteredWs = useMemo(() => {
    // A row passes a facet when no values are selected, or it matches one (OR
    // within a facet, AND across facets).
    const matchesFacets = (w: WsRow) =>
      (wsFacets.deflt.length  === 0 || wsFacets.deflt.includes(w.is_default)) &&
      (wsFacets.active.length === 0 || wsFacets.active.includes(w.is_active));
    const q = wsFilter.trim().toLowerCase();
    const base = !q ? ws : ws.filter(w => w.internal_code.toLowerCase().includes(q) || (w.name ?? '').toLowerCase().includes(q));
    return base.filter(matchesFacets);
  }, [ws, wsFilter, wsFacets]);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [w, c, m, r, ro] = await Promise.all([
        apiDb.from('une_workspace').select('une_workspace_id, internal_code, name, is_default, is_active').order('internal_code'),
        apiDb.from('une_event_category').select('une_event_category_id, internal_code, name, sort_order, is_active').order('sort_order'),
        apiDb.from('une_mandatory_rule').select('une_mandatory_rule_id, une_event_category_id, channel, une_workspace_id, is_active').order('une_mandatory_rule_id'),
        apiDb.from('une_role_default').select('une_role_default_id, une_event_category_id, role_id, une_workspace_id, channel_pref, is_active').order('une_role_default_id'),
        apiDb.from('csm_role_master').select('role_id, internal_code').order('internal_code'),
      ]);
      if (w.error) throw w.error;
      setWs((w.data ?? []) as WsRow[]); setCats((c.data ?? []) as CatRow[]); setMand((m.data ?? []) as MandRow[]); setRds((r.data ?? []) as RdRow[]); setRoles((ro.data ?? []) as RoleLite[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const catName = (id: number) => cats.find(c => c.une_event_category_id === id)?.name ?? '?';

  // §17.B13: related-children (expand/contract) for a workspace row — the mandatory
  // rules and role/global defaults scoped to it (both already loaded above). Names
  // resolve through catName; the raw channel/preference rides along in `title` (§17.B4).
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    {
      key: 'mandatory',
      table: 'une_mandatory_rule', pkColumn: 'une_mandatory_rule_id',
      title: t('une_admin.related.mandatory', 'Mandatory Rules'),
      items: mand.filter(m => m.une_workspace_id === id).map(m => ({
        id: m.une_mandatory_rule_id, label: catName(m.une_event_category_id), title: m.channel,
      })),
    },
    {
      key: 'role_defaults',
      table: 'une_role_default', pkColumn: 'une_role_default_id',
      title: t('une_admin.related.role_defaults', 'Role / Global Defaults'),
      items: rds.filter(r => r.une_workspace_id === id).map(r => ({
        id: r.une_role_default_id, label: catName(r.une_event_category_id), title: r.channel_pref,
      })),
    },
  ];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  // Workspaces
  const addWs = async () => { await routedInsert(session, 'une_workspace', { internal_code: `WS_${Date.now()}`, name: 'New workspace', is_default: 'N', is_active: 'Y' }); await reload(); };
  const saveWs = async (w: WsRow) => { await routedUpdate(session, 'une_workspace', 'une_workspace_id', w.une_workspace_id, { name: w.name, is_default: w.is_default, is_active: w.is_active }); };
  const delWs = async (id: number) => { await routedDelete(session, 'une_workspace', 'une_workspace_id', id); setWs(x => x.filter(r => r.une_workspace_id !== id)); };

  // Categories
  const addCat = async () => { await routedInsert(session, 'une_event_category', { internal_code: `CAT_${Date.now()}`, name: 'New category', sort_order: (cats.length + 1) * 10, is_active: 'Y' }); await reload(); };
  const saveCat = async (c: CatRow) => { await routedUpdate(session, 'une_event_category', 'une_event_category_id', c.une_event_category_id, { name: c.name, sort_order: c.sort_order, is_active: c.is_active }); };
  const delCat = async (id: number) => { await routedDelete(session, 'une_event_category', 'une_event_category_id', id); setCats(x => x.filter(r => r.une_event_category_id !== id)); };

  // Mandatory
  const addMand = async () => { if (!cats[0]) return; await routedInsert(session, 'une_mandatory_rule', { une_event_category_id: cats[0].une_event_category_id, channel: 'INAPP', is_active: 'Y' }); await reload(); };
  const saveMand = async (m: MandRow) => { await routedUpdate(session, 'une_mandatory_rule', 'une_mandatory_rule_id', m.une_mandatory_rule_id, { une_event_category_id: m.une_event_category_id, channel: m.channel, une_workspace_id: m.une_workspace_id, is_active: m.is_active }); };
  const delMand = async (id: number) => { await routedDelete(session, 'une_mandatory_rule', 'une_mandatory_rule_id', id); setMand(x => x.filter(r => r.une_mandatory_rule_id !== id)); };

  // Role / global defaults
  const addRd = async () => { if (!cats[0]) return; await routedInsert(session, 'une_role_default', { une_event_category_id: cats[0].une_event_category_id, channel_pref: 'BOTH', is_active: 'Y' }); await reload(); };
  const saveRd = async (r: RdRow) => { await routedUpdate(session, 'une_role_default', 'une_role_default_id', r.une_role_default_id, { une_event_category_id: r.une_event_category_id, role_id: r.role_id, une_workspace_id: r.une_workspace_id, channel_pref: r.channel_pref, is_active: r.is_active }); };
  const delRd = async (id: number) => { await routedDelete(session, 'une_role_default', 'une_role_default_id', id); setRds(x => x.filter(r => r.une_role_default_id !== id)); };

  const wsOpts = (val: number | null, on: (v: number | null) => void) => (
    <div className="w-40">
      <FilteredModalSingleSelect
        label={t('une_admin.field.workspace', 'Workspace')}
        options={ws.map(w => ({ internal_code: String(w.une_workspace_id), display_text: w.name }))}
        selectedCode={val == null ? null : String(val)}
        onChange={(code) => on(code ? Number(code) : null)}
        clearLabel={t('une_admin.all_workspaces', 'all workspaces')}
      />
    </div>
  );
  const catOpts = (val: number, on: (v: number) => void) => (
    <div className="w-40">
      <FilteredModalSingleSelect
        label={t('une_admin.field.category', 'Category')}
        options={cats.map(c => ({ internal_code: String(c.une_event_category_id), display_text: c.name }))}
        selectedCode={String(val)}
        onChange={(code) => { if (code) on(Number(code)); }}
        allowClear={false}
      />
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('une_admin.title', 'Notification Administration')}</span>
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('une_admin.refresh', 'Refresh')}</button>
        <input type="text" value={wsFilter} onChange={e => setWsFilter(e.target.value)} placeholder={t('une_admin.filter_workspaces', 'Filter workspaces…')} className="h-7 w-44 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none" />
        <FacetFilter label={t('une_admin.col.is_default', 'Default')} options={wsFacetOptions.deflt}  selected={wsFacets.deflt}  onChange={v => setWsFacets(f => ({ ...f, deflt: v }))} />
        <FacetFilter label={t('une_admin.col.is_active', 'Active')}  options={wsFacetOptions.active} selected={wsFacets.active} onChange={v => setWsFacets(f => ({ ...f, active: v }))} />
        <ClearFiltersButton active={anyWsFilterActive} onClear={clearWsFilters} label={t('common.clear_filters', 'Clear filters')} />
        {busy && <span className="text-csm-xs text-csm-watermark">{t('une_admin.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>
      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('une_admin.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <Section title={t('une_admin.section_workspaces', 'Workspaces')} onAdd={addWs}>
          <PersonalizedGrid componentKey="grid" columns={WS_GRID_COLS}>
            {(visibleCols, widths) => (
              <table className="w-max border-collapse text-csm-sm">
                <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                  <th className="w-7 px-2 py-1" title={t('une_admin.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="px-2 py-1" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('une_admin.col.' + col.key, col.header)}</th>
                  ))}
                  <th className="px-2 py-1 text-left" style={{ width: '1%' }}>{t('une_admin.actions', 'Actions')}</th>
                </tr></thead>
                <tbody>
                  {filteredWs.map(w => {
                    const i = ws.indexOf(w);
                    return (
                      <Fragment key={w.une_workspace_id}>
                      <tr className="border-b border-csm-border-soft">
                        <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                          <RelatedExpandButton open={detail.isOpen(w.une_workspace_id)} hasChildren={hasRelated(w.une_workspace_id)} onToggle={() => detail.toggle(w.une_workspace_id)} expandLabel={t('une_admin.related.expand', 'Show related records')} collapseLabel={t('une_admin.related.collapse', 'Hide related')} tooltip={t('une_admin.related.tooltip', 'Show related records')} />
                        </td>
                        {visibleCols.map(col => (
                          <td key={col.key} className="px-2 py-1" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                            {col.key === 'internal_code' && <span className="font-mono text-csm-2xs text-csm-ink-soft">{w.internal_code}</span>}
                            {col.key === 'name'          && <input value={w.name} onChange={e => setWs(xs => xs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} onBlur={() => void saveWs(ws[i])} className="h-7 w-44 rounded border border-csm-border px-1 text-csm-xs" />}
                            {col.key === 'is_default'    && <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={w.is_default === 'Y'} onChange={e => { setWs(xs => xs.map((x, j) => j === i ? { ...x, is_default: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveWs(ws[i])} /> {t('une_admin.default', 'default')}</label>}
                            {col.key === 'is_active'     && <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={w.is_active === 'Y'} onChange={e => { setWs(xs => xs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveWs(ws[i])} /> {t('une_admin.active', 'active')}</label>}
                          </td>
                        ))}
                        <td className="px-2 py-1 text-right">{canDelete && (<button onClick={() => void delWs(w.une_workspace_id)} className="text-csm-danger text-csm-xs">{t('une_admin.del', 'del')}</button>)}</td>
                      </tr>
                      {detail.isOpen(w.une_workspace_id) && (
                        <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                          <RelatedChildrenPanel groups={buildRelatedGroups(w.une_workspace_id)} session={session} />
                        </td></tr>
                      )}
                      </Fragment>
                    );
                  })}
                  {filteredWs.length === 0 && !busy && (
                    <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3">
                      <EmptyState variant={anyWsFilterActive ? 'no-match' : 'no-data'} title={t('une_admin.empty_workspaces', 'No workspaces.')} action={!anyWsFilterActive ? canAdd && (<button onClick={() => void addWs()} className="rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('une_admin.add', '+ Add')}</button>) : undefined} />
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
          </PersonalizedGrid>
        </Section>

        <Section title={t('une_admin.section_event_categories', 'Event Categories')} onAdd={addCat}>
          {cats.map((c, i) => (
            <div key={c.une_event_category_id} className="mb-1 flex flex-wrap items-center gap-1">
              <span className="w-36 font-mono text-csm-2xs text-csm-ink-soft">{c.internal_code}</span>
              <input value={c.name} onChange={e => setCats(xs => xs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} onBlur={() => void saveCat(cats[i])} className="h-7 w-44 rounded border border-csm-border px-1 text-csm-xs" />
              <input type="number" value={c.sort_order} onChange={e => setCats(xs => xs.map((x, j) => j === i ? { ...x, sort_order: Number(e.target.value) } : x))} onBlur={() => void saveCat(cats[i])} className="h-7 w-14 rounded border border-csm-border px-1 text-csm-xs" />
              <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={c.is_active === 'Y'} onChange={e => { setCats(xs => xs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); }} onBlur={() => void saveCat(cats[i])} /> {t('une_admin.active', 'active')}</label>
              {canDelete && (<button onClick={() => void delCat(c.une_event_category_id)} className="text-csm-danger text-csm-xs">{t('une_admin.del', 'del')}</button>)}
            </div>
          ))}
        </Section>

        <Section title={t('une_admin.section_mandatory_rules', 'Mandatory Rules (lock a channel on)')} onAdd={addMand}>
          {mand.map((m, i) => (
            <div key={m.une_mandatory_rule_id} className="mb-1 flex flex-wrap items-center gap-1">
              {catOpts(m.une_event_category_id, v => { setMand(xs => xs.map((x, j) => j === i ? { ...x, une_event_category_id: v } : x)); void saveMand({ ...mand[i], une_event_category_id: v }); })}
              <select value={m.channel} onChange={e => { setMand(xs => xs.map((x, j) => j === i ? { ...x, channel: e.target.value as 'INAPP' | 'EMAIL' } : x)); void saveMand({ ...mand[i], channel: e.target.value as 'INAPP' | 'EMAIL' }); }} className="h-7 rounded border border-csm-border px-1 text-csm-xs"><option value="INAPP">{t('une_admin.channel_inapp', 'In-App')}</option><option value="EMAIL">{t('une_admin.channel_email', 'Email')}</option></select>
              {wsOpts(m.une_workspace_id, v => { setMand(xs => xs.map((x, j) => j === i ? { ...x, une_workspace_id: v } : x)); void saveMand({ ...mand[i], une_workspace_id: v }); })}
              <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={m.is_active === 'Y'} onChange={e => { setMand(xs => xs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); void saveMand({ ...mand[i], is_active: e.target.checked ? 'Y' : 'N' }); }} /> {t('une_admin.active', 'active')}</label>
              {canDelete && (<button onClick={() => void delMand(m.une_mandatory_rule_id)} className="text-csm-danger text-csm-xs">{t('une_admin.del', 'del')}</button>)}
            </div>
          ))}
        </Section>

        <Section title={t('une_admin.section_role_defaults', 'Role / Global Defaults')} onAdd={addRd}>
          {rds.map((r, i) => (
            <div key={r.une_role_default_id} className="mb-1 flex flex-wrap items-center gap-1">
              {catOpts(r.une_event_category_id, v => { setRds(xs => xs.map((x, j) => j === i ? { ...x, une_event_category_id: v } : x)); void saveRd({ ...rds[i], une_event_category_id: v }); })}
              <div className="w-36">
                <FilteredModalSingleSelect
                  label={t('une_admin.field.role', 'Role')}
                  options={roles.map(ro => ({ internal_code: String(ro.role_id), display_text: ro.internal_code }))}
                  selectedCode={r.role_id == null ? null : String(r.role_id)}
                  onChange={(code) => { const v = code ? Number(code) : null; setRds(xs => xs.map((x, j) => j === i ? { ...x, role_id: v } : x)); void saveRd({ ...rds[i], role_id: v }); }}
                  clearLabel={t('une_admin.global', 'global')}
                />
              </div>
              {wsOpts(r.une_workspace_id, v => { setRds(xs => xs.map((x, j) => j === i ? { ...x, une_workspace_id: v } : x)); void saveRd({ ...rds[i], une_workspace_id: v }); })}
              <select value={r.channel_pref} onChange={e => { setRds(xs => xs.map((x, j) => j === i ? { ...x, channel_pref: e.target.value as Pref } : x)); void saveRd({ ...rds[i], channel_pref: e.target.value as Pref }); }} className="h-7 rounded border border-csm-border px-1 text-csm-xs">{['INAPP_ONLY', 'EMAIL_ONLY', 'BOTH', 'NONE'].map(p => <option key={p} value={p}>{p}</option>)}</select>
              <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={r.is_active === 'Y'} onChange={e => { setRds(xs => xs.map((x, j) => j === i ? { ...x, is_active: e.target.checked ? 'Y' : 'N' } : x)); void saveRd({ ...rds[i], is_active: e.target.checked ? 'Y' : 'N' }); }} /> {t('une_admin.active', 'active')}</label>
              <span className="text-csm-2xs text-csm-watermark">{catName(r.une_event_category_id)}</span>
              {canDelete && (<button onClick={() => void delRd(r.une_role_default_id)} className="text-csm-danger text-csm-xs">{t('une_admin.del', 'del')}</button>)}
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mb-4 rounded border border-csm-border bg-white p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-ink-soft">{title}</span>
        <button onClick={onAdd} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">{t('une_admin.add', '+ Add')}</button>
      </div>
      {children}
    </div>
  );
}
